import { Trash2 } from "lucide-react";
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import userApi from "@/api/user";
import { PageWrapper } from "@/components/PageWrapper";
import { Button } from "@/components/ui/button";
import { DataTable, ServerTableColumn } from "@/components/ui/data-table";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { addNotification } from "@/store/app/slice";
import { getIs1TemplateTenant } from "@/store/tenant/selectors";
import { User, NotificationMessageType, NotificationType } from "@/types";
import { getIsProductionEnv } from "@/utils/env";
import { getIsAdminRole } from "@/utils/getIsRole";

type SortColumn =
    | "firstName"
    | "lastName"
    | "email"
    | "role"
    | "createdAt"
    | "isEmailVerified"
    | "creditBalance";

const itemsPerPage = 20;

export const PortalUsersPage = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [sortColumn, setSortColumn] = useState<SortColumn>("firstName");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const hasInitialised = useRef(false);
    const isProduction = getIsProductionEnv();
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);

    const fetchUsers = useCallback(
        async (
            page: number,
            sortCol: SortColumn = sortColumn,
            sortDir: "asc" | "desc" = sortDirection,
            search: string = searchTerm,
        ) => {
            setIsLoading(true);
            try {
                const result = await userApi.getUsersApi({
                    page: page,
                    limit: itemsPerPage,
                    sortBy: `${sortCol}:${sortDir}`,
                    search: search || undefined,
                });
                setUsers(result.results);
                setTotalItems(result.totalResults);
                setCurrentPage(page);
            } catch (error) {
                dispatch(
                    addNotification({
                        title: "Error",
                        desc: "Failed to fetch users",
                        messageType: NotificationMessageType.ADMIN_USER_SAVE,
                        type: NotificationType.ERROR,
                    }),
                );
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, searchTerm, sortColumn, sortDirection],
    );

    useEffect(() => {
        if (!hasInitialised.current) {
            hasInitialised.current = true;
            fetchUsers(1);
        }
    }, [fetchUsers]);

    const handleRowClick = (userId: string) => {
        navigate(`${userId}`);
    };

    const handleDelete = useCallback(
        async (e: React.MouseEvent, userId: string) => {
            e.stopPropagation();
            if (isProduction) {
                dispatch(
                    addNotification({
                        title: "Unable to complete action",
                        desc: "You cannot delete users in production via the admin panel. Removing users must be done via the proper process",
                        messageType: NotificationMessageType.ADMIN_USER_DELETE,
                        type: NotificationType.WARNING,
                    }),
                );
                return;
            }
            try {
                await userApi.deleteUserApi(userId);
                setUsers(prev => prev.filter(user => user.id !== userId));
                dispatch(
                    addNotification({
                        title: "Success",
                        desc: "User deleted successfully",
                        messageType: NotificationMessageType.ADMIN_USER_DELETE,
                        type: NotificationType.SUCCESS,
                    }),
                );
            } catch (err: unknown) {
                dispatch(
                    addNotification({
                        title: "Error",
                        desc: "Failed to delete user",
                        messageType: NotificationMessageType.ADMIN_USER_DELETE,
                        type: NotificationType.ERROR,
                    }),
                );
            }
        },
        [dispatch, isProduction],
    );

    const handlePageChange = (page: number) => {
        fetchUsers(page);
    };

    const formatDate = (date: Date) => {
        return new Date(date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const columns: ServerTableColumn<User>[] = [
        {
            key: "firstName",
            label: "First Name",
            width: "w-24",
            sortable: true,
        },
        {
            key: "lastName",
            label: "Last Name",
            width: "w-24",
            sortable: true,
        },
        {
            key: "email",
            label: "Email",
            width: "w-48",
            sortable: true,
        },
        {
            key: "role",
            label: "Role",
            width: "w-20",
            sortable: true,
            render: user => (
                <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        getIsAdminRole(user.role)
                            ? "bg-red-100 text-red-800"
                            : "bg-blue-100 text-blue-800"
                    }`}
                >
                    {user.role}
                </span>
            ),
        },
        {
            key: "createdAt",
            label: "Created",
            width: "w-32",
            sortable: true,
            render: user => formatDate(user.createdAt),
        },
        {
            key: "isEmailVerified",
            label: "Verified?",
            width: "w-20",
            sortable: true,
            render: user => (
                <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        user.isEmailVerified
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                    }`}
                >
                    {user.isEmailVerified ? "Yes" : "No"}
                </span>
            ),
        },
        ...(is1TemplateTenant
            ? [
                  {
                      key: "creditBalance",
                      label: "Credits",
                      width: "w-16",
                      sortable: true,
                      render: user => (
                          <span className="block truncate">
                              {user.credits?.balance ?? 0}
                          </span>
                      ),
                  },
                  {
                      key: "scheduledDeletionDate",
                      label: "Deleting?",
                      width: "w-20",
                      sortable: true,
                      render: user => (
                          <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                  user.scheduledDeletionDate
                                      ? "bg-red-100 text-red-800"
                                      : "bg-neutral-100 text-neutral-800"
                              }`}
                          >
                              {user.scheduledDeletionDate ? "Yes" : "-"}
                          </span>
                      ),
                  },
                  {
                      key: "actions",
                      label: "Actions",
                      width: "w-16",
                      render: user => (
                          <Button
                              variant="ghost"
                              size="sm"
                              onClick={e => {
                                  e.stopPropagation();
                                  handleDelete(e, user.id);
                              }}
                              className="text-red-600 hover:text-red-900"
                          >
                              <Trash2 className="h-4 w-4" />
                          </Button>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <PageWrapper
            title="User Management"
            description="A list of all users including their name, email, role and email verification status."
            primaryAction={
                is1TemplateTenant && (
                    <Button onClick={() => navigate("new")}>Add User</Button>
                )
            }
        >
            <div className="flex-1 overflow-hidden">
                <div className="flex h-full flex-col rounded-lg bg-admin-card shadow-sm">
                    <div className="flex-1 overflow-hidden px-6 py-4">
                        <DataTable
                            data={users}
                            totalItems={totalItems}
                            isLoading={isLoading}
                            currentPage={currentPage}
                            pageSize={itemsPerPage}
                            onPageChange={handlePageChange}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSortChange={(column, direction) => {
                                setSortColumn(column as SortColumn);
                                setSortDirection(direction);
                                setCurrentPage(1);
                                fetchUsers(1, column as SortColumn, direction);
                            }}
                            searchTerm={searchTerm}
                            onSearchChange={term => {
                                setSearchTerm(term);
                                setCurrentPage(1);
                                fetchUsers(1, sortColumn, sortDirection, term);
                            }}
                            searchPlaceholder="Search users..."
                            columns={columns}
                            onRowClick={user => handleRowClick(user.id)}
                            getRowKey={user => user.id}
                        />
                    </div>
                </div>
            </div>
        </PageWrapper>
    );
};
